<script setup>
// Dialog inline para criar tarefa avulsa em qualquer lead.
// Usado em "Minhas tarefas" pra que o agente possa lançar uma tarefa sem
// precisar abrir o LeadDrawer manualmente.
import { ref, computed, watch } from 'vue';
import LeadsAPI from 'dashboard/api/leads';
import { useLeadActivitiesStore } from 'dashboard/stores/leadActivities';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';

const emit = defineEmits(['created']);
const store = useLeadActivitiesStore();

const dialogRef = ref(null);
const isSubmitting = ref(false);
const isSearching = ref(false);
const leadOptions = ref([]);
const leadQuery = ref('');

const form = ref({
  lead_id: null,
  title: '',
  kind: 'call',
  due_at: '',
  description: '',
});

const KIND_OPTIONS = [
  { value: 'call',     label: 'Ligação',  icon: 'i-lucide-phone' },
  { value: 'email',    label: 'E-mail',   icon: 'i-lucide-mail' },
  { value: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { value: 'visit',    label: 'Visita',   icon: 'i-lucide-map-pin' },
  { value: 'meeting',  label: 'Reunião',  icon: 'i-lucide-calendar' },
  { value: 'note',     label: 'Nota',     icon: 'i-lucide-sticky-note' },
];

// Opções p/ SelectInput (descarta icon, padrão {value,label}).
const kindSelectOptions = computed(() =>
  KIND_OPTIONS.map(k => ({ value: k.value, label: k.label }))
);

const canSubmit = computed(() =>
  Boolean(form.value.lead_id) &&
  form.value.title.trim().length >= 2 &&
  Boolean(form.value.due_at)
);

function resetForm() {
  form.value = {
    lead_id: null,
    title: '',
    kind: 'call',
    due_at: defaultDueAt(),
    description: '',
  };
  leadQuery.value = '';
  leadOptions.value = [];
}

function defaultDueAt() {
  // Próximo "horário cheio" do dia atual (ex: agora=14h32 → 15:00). Útil pra
  // tarefa rápida onde o agente vai ajustar só se quiser.
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d.toISOString().slice(0, 16);
}

defineExpose({
  open(prefillLeadId = null) {
    resetForm();
    if (prefillLeadId) form.value.lead_id = prefillLeadId;
    dialogRef.value?.open();
  },
  close() { dialogRef.value?.close(); },
});

// Busca de lead (debounce simples). Backend espera q via LeadsAPI.index.
let searchTimer = null;
watch(leadQuery, async (q) => {
  if (searchTimer) clearTimeout(searchTimer);
  if (!q || q.trim().length < 2) {
    leadOptions.value = [];
    return;
  }
  searchTimer = setTimeout(async () => {
    isSearching.value = true;
    try {
      const { data } = await LeadsAPI.index({ q: q.trim(), per_page: 10 });
      leadOptions.value = data?.leads || [];
    } catch {
      leadOptions.value = [];
    } finally {
      isSearching.value = false;
    }
  }, 250);
});

function pickLead(lead) {
  form.value.lead_id = lead.id;
  leadQuery.value = `${lead.contact?.name || 'Lead'} · ${lead.pipeline?.name || '-'}`;
  leadOptions.value = [];
}

async function submit() {
  if (!canSubmit.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    const dueIso = new Date(form.value.due_at).toISOString();
    const payload = {
      activity: {
        title: form.value.title.trim(),
        kind: form.value.kind,
        due_at: dueIso,
        // Reminder 1h antes do due_at (padrão consistente com o Materializer).
        reminder_at: new Date(new Date(form.value.due_at).getTime() - 3600000).toISOString(),
        description: form.value.description || null,
      },
    };
    const created = await store.create(form.value.lead_id, payload);
    emit('created', created);
    useAlert('Tarefa criada.');
    dialogRef.value?.close();
  } catch (e) {
    useAlert(
      e?.response?.data?.error
        || e?.response?.data?.errors?.join?.(', ')
        || 'Falha ao criar tarefa.'
    );
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <div>
        <h3 class="text-heading-3 text-n-slate-12 mb-1">Nova tarefa</h3>
        <p class="text-body-small text-n-slate-11 mb-0">
          Lance uma tarefa avulsa em qualquer lead. Útil pra rastrear toques fora dos modelos de follow-up.
        </p>
      </div>

      <!-- Buscar lead -->
      <div class="relative">
        <label class="text-body-small text-n-slate-12 font-medium block mb-1">
          Lead
        </label>
        <div class="relative">
          <Input
            :model-value="leadQuery"
            type="search"
            placeholder="Digite o nome do contato para buscar..."
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
            ]"
            class="w-full"
            @input="leadQuery = $event.target.value"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
              />
            </template>
          </Input>
        </div>
        <div
          v-if="leadOptions.length"
          class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-n-weak bg-n-solid-1 shadow-lg"
        >
          <button
            v-for="l in leadOptions"
            :key="l.id"
            type="button"
            class="w-full px-3 py-2 text-left hover:bg-n-alpha-1 flex items-center justify-between gap-2 border-b border-n-weak last:border-b-0"
            @click="pickLead(l)"
          >
            <div class="min-w-0">
              <p class="text-body-main text-n-slate-12 truncate mb-0">{{ l.contact?.name || 'Lead' }}</p>
              <p class="text-body-small text-n-slate-11 mb-0 truncate">
                {{ l.pipeline?.name || '-' }} · {{ l.stage?.name || l.status }}
              </p>
            </div>
            <span class="text-label-small text-n-slate-10">#{{ l.id }}</span>
          </button>
        </div>
        <p v-if="form.lead_id" class="text-body-small text-n-grass-11 mt-1 mb-0">
          ✓ Lead selecionado.
        </p>
      </div>

      <Input
        v-model="form.title"
        label="Título"
        placeholder="Ex: Ligar para confirmar proposta"
      />

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-body-small text-n-slate-12 font-medium mb-0.5">Tipo</label>
          <SelectInput v-model="form.kind" :options="kindSelectOptions" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-body-small text-n-slate-12 font-medium mb-0.5">Vencimento</label>
          <input
            v-model="form.due_at"
            type="datetime-local"
            class="w-full h-10 px-3 text-sm rounded-lg border-none outline outline-1 outline-offset-[-1px] bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand transition-all duration-200 !mb-0"
          />
        </div>
      </div>

      <div>
        <label class="text-body-small text-n-slate-12 font-medium block mb-1">Descrição</label>
        <textarea
          v-model="form.description"
          rows="2"
          placeholder="Opcional - instruções, contexto, números pra ligar..."
          class="w-full rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-body-main text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand resize-none"
        />
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <NextButton faded slate label="Cancelar" @click="dialogRef?.close()" />
        <NextButton
          :is-loading="isSubmitting"
          :disabled="!canSubmit"
          label="Criar tarefa"
          icon="i-lucide-plus"
          @click="submit"
        />
      </div>
    </div>
  </Dialog>
</template>
